/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import UserSegmentToggle from '../common/UserSegmentToggle';
import { useUserSegment, watchParam, watchFilterValue } from '../../context/UserSegmentContext';
import DateRangeBar from './DateRangeBar';
import ActiveUsersSummaryCards from './ActiveUsersSummaryCards';
import ActiveUsersTrendChart from './ActiveUsersTrendChart';
import DailyActivityTable from './DailyActivityTable';
import ActiveUsersVersionModal from './ActiveUsersVersionModal';
import WauUsersModal from './WauUsersModal';
import ActiveUsersVersionPanel from './ActiveUsersVersionPanel';
import ExportButton from './ExportButton';
import EmptyState from './states/EmptyState';
import ErrorState from './states/ErrorState';
import TableSkeleton from './states/TableSkeleton';
import './active-users.css';

const LIMIT = 20;
const CHART_LIMIT = 366; // enough days to draw a trend for the current range — and the CSV export

// The "Active users per day · by platform" chart is hidden at the client's request (2026-09-28).
// Its per-day rows still load: the CSV export downloads them. Flip this to true to bring the
// chart back; nothing else has to change.
const SHOW_TREND_CHART = false;

const DAILY_COLUMNS = [
  { key: 'access_date', label: 'Date (UTC)' },
  { key: 'android_users', label: 'Android' },
  { key: 'ios_users', label: 'iOS' },
  { key: 'android_watch_users', label: 'Android watch' },
  { key: 'ios_watch_users', label: 'iOS watch' },
  { key: 'total_users', label: 'Total' },
];

const DEVICE_KEYS = ['android', 'ios', 'android_watch', 'ios_watch'];

// Previous equal-length window immediately preceding [from, to].
export const prevWindow = (from, to) => {
  const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
  const fmt = (dt) => dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
  const f = parse(from);
  const t = parse(to);
  const len = Math.round((t - f) / 86400000) + 1;
  const prevTo = new Date(f); prevTo.setDate(prevTo.getDate() - 1);
  const prevFrom = new Date(prevTo); prevFrom.setDate(prevFrom.getDate() - (len - 1));
  return { prevFrom: fmt(prevFrom), prevTo: fmt(prevTo) };
};

// Signed % change per device total vs the previous window (null when no base).
export const computeDeltas = (cur, prev) => {
  if (!cur || !prev) return null;
  const out = {};
  DEVICE_KEYS.forEach((k) => {
    const c = Number(cur[`total_${k}`]) || 0;
    const p = Number(prev[`total_${k}`]) || 0;
    out[k] = p > 0 ? Math.round(((c - p) / p) * 100) : null;
  });
  return out;
};

const DailyActiveUsers = () => {
  const [name] = useState('Pauser');
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [rows, setRows] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [summaryDeltas, setSummaryDeltas] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFrom, setDisplayFrom] = useState(null);
  const [displayTo, setDisplayTo] = useState(null);
  const [preset, setPreset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [versionCtx, setVersionCtx] = useState(null);   // drill-down popup context (feature 053)
  const [wauOpen, setWauOpen] = useState(false);        // WAU users popup (feature 129)
  const navigate = useNavigate();
  const { segment } = useUserSegment();   // global watch/all segment (feature 023)

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      const now = new Date();
      if (expire * 1000 < now.getTime()) {
        const r = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${r.data.accessToken}`;
        setToken(r.data.accessToken);
        setExpire(jwtDecode(r.data.accessToken).exp);
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  useEffect(() => { refreshToken(); }, []);
  useEffect(() => { load(1, '', ''); }, []);

  // Re-fetch every panel when the global segment changes (skip initial mount).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    setPage(1);
    load(1, fromDate, toDate);
  }, [segment]);

  const refreshToken = async () => {
    try {
      const r = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(r.data.accessToken);
      setExpire(jwtDecode(r.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  // Table page (paginated, descending).
  const fetchTable = async (pg, from, to) => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/get-user-app-activity-statastics?page=${pg}&limit=${LIMIT}&from_date=${from}&to_date=${to}${watchParam(segment)}`,
      auth()
    );
    setRows(res.data.data || []);
    setTotalItem(res.data.totalItem || 0);
  };

  // Full-range series for the chart (ascending).
  const fetchChart = async (from, to) => {
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/get-user-app-activity-statastics?page=1&limit=${CHART_LIMIT}&from_date=${from}&to_date=${to}${watchParam(segment)}`,
        auth()
      );
      const series = [...(res.data.data || [])].sort((a, b) =>
        String(a.access_date).localeCompare(String(b.access_date))
      );
      setChartData(series);
    } catch { setChartData([]); }
  };

  // Device totals + avg/day (only meaningful with a range).
  const fetchSummaryRaw = async (from, to) => {
    if (!from || !to) return null;
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/get-device-activity-summary?from_date=${from}&to_date=${to}${watchParam(segment)}`,
        auth()
      );
      return res.data && res.data.success ? res.data.data : null;
    } catch { return null; }
  };

  const fetchSummary = async (from, to) => {
    const data = await fetchSummaryRaw(from, to);
    setSummary(data);
    return data;
  };

  // Range-anchored DAU/WAU/MAU (US3 endpoint). Degrades gracefully if absent.
  const fetchMetrics = async (anchor) => {
    try {
      const parts = [];
      if (anchor) parts.push(`anchor_date=${anchor}`);
      const wf = watchFilterValue(segment);
      if (wf) parts.push(`watch_filter=${wf}`);
      const q = parts.length ? `?${parts.join('&')}` : '';
      const res = await axiosJWT.get(`${baseUrl}/admin/api/get-active-users-metrics${q}`, auth());
      setMetrics(res.data && res.data.success ? res.data.data : null);
    } catch { setMetrics(null); }
  };

  const load = async (pg, from, to) => {
    setLoading(true);
    setError('');
    try {
      await fetchTable(pg, from, to);
      await Promise.all([fetchChart(from, to), fetchMetrics(to || '')]);
      const cur = await fetchSummary(from, to);
      if (from && to && cur) {
        const { prevFrom, prevTo } = prevWindow(from, to);
        const prev = await fetchSummaryRaw(prevFrom, prevTo);
        setSummaryDeltas(computeDeltas(cur, prev));
      } else {
        setSummaryDeltas(null);
      }
    } catch (err) {
      if (err.response && err.response.status === 401) { navigate('/'); return; }
      setError('We couldn’t load daily active users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyRange = (from, to) => { setPage(1); load(1, from, to); };

  const handlePreset = (key, fromStr, toStr) => {
    setPreset(key);
    setFromDate(fromStr); setToDate(toStr);
    setDisplayFrom(new Date(fromStr)); setDisplayTo(new Date(toStr));
    applyRange(fromStr, toStr);
  };

  const fmt = (d) =>
    d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

  const handleFrom = (d) => { setPreset('custom'); setDisplayFrom(d); setFromDate(fmt(d)); };
  const handleTo = (d) => { setPreset('custom'); setDisplayTo(d); setToDate(fmt(d)); };
  const handleSearch = () => applyRange(fromDate, toDate);
  const handlePage = (pg) => { setPage(pg); fetchTable(pg, fromDate, toDate); };

  const clearFilter = () => {
    setPreset(null);
    setFromDate(''); setToDate(''); setDisplayFrom(null); setDisplayTo(null);
    setPage(1); setSummary(null);
    load(1, '', '');
  };

  const hasRange = Boolean(fromDate || toDate);

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name={name} />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">People › <b>Daily active users</b></span>
            <span className="au-route">/active-users</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Daily active users</h1>

          <DateRangeBar
            preset={preset}
            displayFrom={displayFrom}
            displayTo={displayTo}
            hasRange={hasRange}
            onPreset={handlePreset}
            onFromChange={handleFrom}
            onToChange={handleTo}
            onSearch={handleSearch}
            onClear={clearFilter}
          />

          <ActiveUsersSummaryCards
            metrics={metrics}
            summary={summary}
            summaryDeltas={summaryDeltas}
            onViewWauUsers={() => setWauOpen(true)}
          />

          {SHOW_TREND_CHART && <ActiveUsersTrendChart data={chartData} />}

          <ActiveUsersVersionPanel segment={segment} />

          {loading ? (
            <div className="au-panel"><TableSkeleton rows={8} /></div>
          ) : error ? (
            <ErrorState message={error} onRetry={() => load(page, fromDate, toDate)} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No active-user data"
              hasFilters={hasRange}
              onClear={clearFilter}
              message={hasRange ? 'No activity in the selected range.' : 'There is no activity to show yet.'}
            />
          ) : (
            <div className="au-panel">
              <div className="au-panel__head">
                <h4 className="au-panel__title">Daily breakdown</h4>
                <span className="au-spacer" />
                <ExportButton columns={DAILY_COLUMNS} rows={chartData} filename="daily-active-users.csv" />
              </div>
              <DailyActivityTable
                rows={rows}
                onCellClick={(date, platform, count) => setVersionCtx({ date, platform, count })}
              />
              {totalItem > LIMIT && (
                <Pagination
                  activePage={page}
                  itemsCountPerPage={LIMIT}
                  totalItemsCount={totalItem}
                  pageRangeDisplayed={5}
                  onChange={handlePage}
                />
              )}
            </div>
          )}

          <ActiveUsersVersionModal
            open={!!versionCtx}
            ctx={versionCtx}
            segment={segment}
            onClose={() => setVersionCtx(null)}
          />

          {/* Anchored on the date the WAU card itself reports, so the list total matches it. */}
          <WauUsersModal
            open={wauOpen && !!metrics}
            anchorDate={metrics?.anchor_date}
            wau={metrics?.wau}
            segment={segment}
            onClose={() => setWauOpen(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default DailyActiveUsers;
